import {
  Box,
  Button, Dialog,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  FileFragmentFragment,
  FileType,
  useAnalyzeFileMutation,
  useCreateFileFromTextMutation,
  ZyusetuFieldSectionFragmentFragment,
  ZyusetuType,
} from "@/graphql/graphql";

import toast from "react-hot-toast";
import {useState} from "react";
import {FileTypeConfig, FileTypeLabel} from "@/types/FileType.tsx";
import {Upload} from "lucide-react";
import {useDropzone} from "react-dropzone";
import {uploadFile} from "@/graphql/api.tsx";
import {useAuth0} from "@auth0/auth0-react";
import {LoadingCard} from "@/components/LoadingCard.tsx";

interface FileUploaderProps {
  fileType: FileTypeConfig;
  onAnalyzeComplete: (result: ZyusetuFieldSectionFragmentFragment[], file: FileFragmentFragment) => void;
  zyusetuType: ZyusetuType;
}

export function FileUploader({fileType, onAnalyzeComplete, zyusetuType}: FileUploaderProps) {
  const analyze = useAnalyzeFileMutation();
  const createFileFromText = useCreateFileFromTextMutation();
  const [loading, setLoading] = useState(false);
  const [reinsText, setReinsText] = useState<string>("");
  const {getAccessTokenSilently} = useAuth0();
  
  const handleAnalyze = async (file: FileFragmentFragment, loadingId: string) => {
    try {
      const result = await analyze.mutateAsync({
        fileId: file.id,
        zyusetuType,
      });
      
      if (!result.analyzeFile) {
        toast.error("ファイルの解析に失敗しました。しばらくしてから再度お試しください", {id: loadingId});
        return;
      }
      
      toast.success("ファイルの解析が完了しました", {id: loadingId});
      onAnalyzeComplete(result.analyzeFile.fields || [], result.analyzeFile.file);
    } catch (e) {
      console.error(e);
      toast.error("ファイルの解析に失敗しました。しばらくしてから再度お試しください", {id: loadingId});
    }
  };
  
  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    setLoading(true);
    
    const loadingId = toast.loading("ファイルをアップロードしています");
    const uploadedFile = await uploadFile(
      file,
      fileType.type,
      await getAccessTokenSilently({
        authorizationParams: {
          audience: "ai-zyusetu"
        }
      })
    );
    
    if (!uploadedFile) {
      toast.error("ファイルのアップロードに失敗しました", {id: loadingId});
      setLoading(false);
      return;
    }
    
    try {
      toast.loading("ファイルを処理しています", {id: loadingId});
      await handleAnalyze(uploadedFile, loadingId);
    } catch {
      toast.error("ファイルの処理に失敗しました", {id: loadingId});
    } finally {
      setLoading(false);
    }
  };
  
  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    disabled: loading,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    multiple: false,
  });
  
  if (fileType.type === FileType.ReinsText) {
    return (
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          border: '2px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        
        <Dialog open={loading} fullWidth maxWidth="xs">
          <Box sx={{
            overflow: 'hidden',
          }}>
            <LoadingCard/>
          </Box>
        </Dialog>
        <TextField
          multiline
          rows={10}
          fullWidth
          placeholder="Reinsの物件詳細ページでCTRL+Aで全選択してコピーして、ここに貼り付けてください"
          disabled={loading}
          value={reinsText}
          onChange={(e) => setReinsText(e.target.value)}
        />
        <Box sx={{mt: 2, display: "flex", justifyContent: "flex-end"}}>
          <Button
            variant="contained"
            disabled={loading || !reinsText}
            onClick={async () => {
              const loadingId = toast.loading("ファイルの解析中...");
              try {
                setLoading(true);
                const file = await createFileFromText.mutateAsync({
                  text: reinsText,
                  fileType: FileType.ReinsText,
                  title: "Reins掲載情報",
                  zyusetuId: "",
                });
                
                if (!file.createFileFromText) {
                  toast.error("ファイルの作成に失敗しました", {id: loadingId});
                  return;
                }
                
                const result = await analyze.mutateAsync({
                  fileId: file.createFileFromText.id,
                  zyusetuType,
                });
                
                if (!result.analyzeFile) {
                  toast.error("ファイルの解析に失敗しました", {id: loadingId});
                  return;
                }
                
                toast.success("ファイルの解析が完了しました", {id: loadingId});
                onAnalyzeComplete(result.analyzeFile.fields || [], result.analyzeFile.file);
              } catch (e) {
                console.error(e);
                toast.error("ファイルの解析に失敗しました", {id: loadingId});
              } finally {
                setLoading(false);
              }
            }}
          >
            解析開始
          </Button>
        </Box>
      </Paper>
    );
  }
  
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        cursor: loading ? "not-allowed" : "pointer",
      }}
    >
      
      <Dialog open={loading} fullWidth maxWidth="xs">
        <Box sx={{
          overflow: 'hidden',
        }}>
          <LoadingCard/>
        </Box>
      </Dialog>
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "border",
          borderRadius: "lg",
          "&:hover": {
            borderColor: loading ? undefined : 'primary.main',
          },
          p: 4,
          textAlign: "center",
        }}
      >
        <input {...getInputProps()} />
        <Upload/>
        <Typography
          sx={{
            mt: 2,
            color: "text.secondary",
          }}
        >
          {isDragActive
            ? `${FileTypeLabel[fileType.type]}をここに入れてください`
            : `${FileTypeLabel[fileType.type]}をドラッグするか、クリックして選択してください`}
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            mt: 1,
          }}
        >
          以下の形式がサポートされています：PDF, PNG, JPEG
        </Typography>
      </Box>
      
      {loading && (
        <Box sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <Typography sx={{color: "text.secondary"}}>
            処理中...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
